import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { ArrowDown, ChevronDown, ChevronRight, RefreshCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { SidebarTree, type SidebarTreeItem } from "./SidebarTree";
import { usePermissions } from "@/hooks/usePermissions";
import type { Permission } from "@/lib/permissions";

export type SidebarNavItem = {
  to: string;
  label: string;
  icon?: LucideIcon;
};

export type UnifiedNavItem = {
  id?: string;
  to?: string;
  label: string;
  icon?: LucideIcon;
  section?: string;
  children?: UnifiedNavItem[];
  end?: boolean;
  permission?: Permission;
};

export type SidebarProps = {
  navItems: UnifiedNavItem[];
  onNavigate?: () => void;
};

type SidebarContentProps = SidebarProps & {
  isCollapsed?: boolean;
};

const createSectionKey = (item: UnifiedNavItem) =>
  item.id || item.label.toLowerCase().replace(/\s+/g, "-");

const makeTreeItem = (item: UnifiedNavItem): SidebarTreeItem => ({
  id: createSectionKey(item),
  label: item.label,
  icon: item.icon,
  to: item.to,
  children: item.children?.map((child) => makeTreeItem(child)),
});

const groupItemsBySection = (items: UnifiedNavItem[]) => {
  const sectionsMap = new Map<string, UnifiedNavItem[]>();
  const sectionOrder: string[] = [];
  const ungrouped: UnifiedNavItem[] = [];

  items.forEach((item) => {
    if (item.section) {
      if (!sectionsMap.has(item.section)) {
        sectionOrder.push(item.section);
        sectionsMap.set(item.section, []);
      }
      sectionsMap.get(item.section)!.push(item);
      return;
    }

    ungrouped.push(item);
  });

  const sections = sectionOrder.map((title) => ({
    title,
    items: sectionsMap.get(title) ?? [],
  }));

  return { sections, ungrouped };
};

export const SidebarContent = ({
  navItems,
  onNavigate,
  isCollapsed,
}: SidebarContentProps) => {
  const collapsed = Boolean(isCollapsed);
  const { hasPermission } = usePermissions();

  const filteredNavItems = useMemo(() => {
    const filterItems = (items: UnifiedNavItem[]): UnifiedNavItem[] => {
      return items
        .filter((item) => !item.permission || hasPermission(item.permission))
        .map((item) => ({
          ...item,
          children: item.children ? filterItems(item.children) : undefined,
        }))
        .filter(
          (item) =>
            !item.children || (item.children && item.children.length > 0),
        );
    };
    return filterItems(navItems);
  }, [navItems, hasPermission]);

  const { sections, ungrouped } = useMemo(
    () => groupItemsBySection(filteredNavItems),
    [filteredNavItems],
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sectionState, setSectionState] = useState<Record<string, boolean>>({});
  const navRef = useRef<HTMLDivElement>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const checkScroll = () => {
    const nav = navRef.current;
    if (!nav) return;

    const { scrollTop, scrollHeight, clientHeight } = nav;

    setCanScrollDown(
      scrollHeight > clientHeight &&
        scrollTop + clientHeight < scrollHeight - 5,
    );
  };

  useEffect(() => {
    setSectionState((prev) => {
      let changed = false;
      const next = { ...prev };
      sections.forEach(({ title }) => {
        if (typeof next[title] === "undefined") {
          next[title] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sections]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    checkScroll();
    nav.addEventListener("scroll", checkScroll);

    window.addEventListener("resize", checkScroll);

    return () => {
      nav.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [sections, ungrouped, sectionState]);

  const toggleSection = (title: string) => {
    setSectionState((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const renderNavItem = (item: UnifiedNavItem) => {
    if (item.children && item.children.length > 0) {
      const treeItems = [makeTreeItem(item)];
      return (
        <SidebarTree
          key={createSectionKey(item)}
          items={treeItems}
          onNavigate={onNavigate}
        />
      );
    }

    const Icon = item.icon;
    const key = item.to || createSectionKey(item);

    return (
      <NavLink
        key={key}
        to={item.to || "#"}
        end={item.end ?? true}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
        className={({ isActive }) =>
          cn(
            "group relative flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-normal text-neutral-700 transition-all duration-normal ease-standard hover:bg-primary-50 hover:text-primary-700",
            isActive && "bg-primary-50 text-primary-500 font-medium",
            collapsed && "justify-center px-0 py-2",
          )
        }
      >
        {({ isActive }) => (
          <>
            {Icon && (
              <Icon
                className={cn(
                  "h-5 w-5 text-neutral-600 transition-colors duration-normal ease-standard group-hover:text-primary-500",
                  isActive && "text-primary-500",
                )}
              />
            )}
            <span
              className={cn(
                "truncate transition-opacity duration-150",
                collapsed && "sr-only",
              )}
            >
              {item.label}
            </span>
          </>
        )}
      </NavLink>
    );
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  return (
    <div className={cn("relative flex h-full flex-col bg-white")}>
      <div className={cn("pb-5 pt-6", collapsed ? "px-3" : "px-6")}>
        <div
          className={cn(
            "flex items-center gap-3",
            collapsed && "justify-center",
          )}
        >
          <img
            src="/prima-larvae-logo.svg"
            alt="Prima Larvae Bali"
            className="h-12 w-auto"
          />
        </div>
      </div>

      <nav
        ref={navRef}
        className={cn(
          "flex flex-1 flex-col gap-6 overflow-y-auto py-6 scrollbar-none",
          collapsed ? "px-2" : "px-5",
        )}
      >
        {ungrouped.length > 0 && (
          <div className="space-y-2">
            <div
              className={cn(
                "flex flex-col gap-1",
                collapsed && "items-center gap-2",
              )}
            >
              {ungrouped.map(renderNavItem)}
            </div>
          </div>
        )}

        {sections.map(({ title, items }) => {
          const isOpen = sectionState[title] ?? true;

          return (
            <div key={title} className="space-y-2">
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => toggleSection(title)}
                  className="flex w-full items-center justify-between rounded-lg px-1 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-disabled"
                >
                  <span>{title}</span>
                  <span className="text-disabled">
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </span>
                </button>
              )}
              <div
                className={cn(
                  "flex flex-col gap-1",
                  !collapsed && !isOpen && "hidden",
                  collapsed && "items-center gap-2",
                )}
              >
                {items.map(renderNavItem)}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Scroll down indicator */}
      {!collapsed && canScrollDown && (
        <div className="pointer-events-none absolute bottom-[88px] left-0 right-0 flex justify-center pb-4">
          <button
            type="button"
            className="pointer-events-auto flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-1.5 text-xs font-medium text-neutral-600 shadow-sm transition-colors hover:bg-neutral-50"
            onClick={() => {
              const nav = navRef.current;
              if (nav) nav.scrollBy({ top: 100, behavior: "smooth" });
            }}
          >
            <ArrowDown className="h-3.5 w-3.5" />
            <span>Scroll down</span>
          </button>
        </div>
      )}

      <div
        className={cn(
          "border-t border-neutral-200 py-5",
          collapsed ? "px-2" : "px-6",
        )}
      >
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-normal text-neutral-700 transition-colors duration-normal ease-standard hover:bg-primary-50 hover:text-primary-700 disabled:cursor-not-allowed",
            collapsed && "justify-center px-0",
          )}
        >
          <RefreshCcw
            className={cn(
              "h-5 w-5 text-neutral-600 transition-colors duration-normal ease-standard",
              isRefreshing && "animate-spin text-primary-500",
            )}
          />
          <span className={cn(collapsed && "sr-only")}>
            {isRefreshing ? "Refreshing…" : "Refresh"}
          </span>
        </button>
      </div>
    </div>
  );
};

export const Sidebar = ({ navItems, onNavigate }: SidebarProps) => {
  return (
    <aside
      className={cn(
        "fixed inset-y-0 z-40 hidden flex-col border-r border-border-default bg-white transition-[width] duration-200 ease-out lg:flex w-[260px]",
      )}
    >
      <SidebarContent navItems={navItems} onNavigate={onNavigate} />
    </aside>
  );
};
